import React, { useState, useEffect, useRef } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField, 
  IconButton, 
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Badge,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  FormControlLabel,
  Switch
} from '@mui/material';
import { 
  ChatOutlined, 
  Send as SendIcon,
  Search as SearchIcon,
  GroupAdd as GroupAddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Close as CloseIcon,
  PersonSearch as PersonSearchIcon,
  Image as ImageIcon,
  Poll as PollIcon,
  Event as EventIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarMonthIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import CustomDatePicker from './CustomDatePicker';
import CustomTimePicker from './CustomTimePicker';
import { usePatientContext } from '../contexts/PatientContext';
import { createApiUrl } from '../utils/apiConfig';
import { getCurrentUser } from '../utils/sessionService';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getAuthHeaders } from '../utils/authUtils';
import { useSnackbar } from './CentralSnackbar';

const Chat = ({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const { currentContext, createContextLink, hasContext } = usePatientContext();
  const { showError, showSuccess } = useSnackbar();
  const { unreadCount: globalUnreadCount, newMessage, workstationUpdate, clearNewMessage, clearWorkstationUpdate, refreshUnreadCount } = useWebSocket(); // WebSocket für Live-Updates
  
  // Current user
  const currentUser = getCurrentUser();
  const isPatientContextDisabled = currentUser?.group_id === 0 || currentUser?.group_id === 6;
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = useState(null);
  
  // Patientenkontext-Link state
  const [pendingLink, setPendingLink] = useState(null);
  
  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  
  // Creation mode: null = normal, 'direct' = creating direct chat, 'group' = creating group
  const [creationMode, setCreationMode] = useState(null);
  
  // User search and selection
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableWorkstations, setAvailableWorkstations] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]); // For group creation
  const [selectedWorkstation, setSelectedWorkstation] = useState(null); // For workstation messaging
  const [groupName, setGroupName] = useState('');
  const [message, setMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const imageInputRef = useRef(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogSrc, setImageDialogSrc] = useState(null);
  
  // Survey dialog states
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [surveyQuestion, setSurveyQuestion] = useState('');
  const [surveyOptions, setSurveyOptions] = useState(['']);
  const [surveyMultipleChoice, setSurveyMultipleChoice] = useState(false);
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0);
  
  // Survey voting state - stores user votes per message
  const [surveyVotes, setSurveyVotes] = useState({});
  
  // Survey voters dialog
  const [votersDialogOpen, setVotersDialogOpen] = useState(false);
  const [selectedVoters, setSelectedVoters] = useState([]);
  const [selectedOptionText, setSelectedOptionText] = useState('');
  
  // Event dialog states
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  
  const [messages, setMessages] = useState([]);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations on mount
  useEffect(() => {
    if (open) {
      loadConversations();
      loadAvailableUsers();
      loadAvailableWorkstations();
    }
  }, [open]);

  // WebSocket: Reagiere auf neue Nachrichten und aktualisiere Konversationsliste
  useEffect(() => {
    console.log('🔔 CHAT WEBSOCKET: useEffect triggered', {
      hasNewMessage: !!newMessage,
      isOpen: open,
      activeConversation,
      newMessageConvId: newMessage?.conversation_id
    });
    
    if (newMessage && newMessage.type === 'new_message') {
      console.log('💬 Chat: Neue Nachricht empfangen via WebSocket, aktualisiere Konversationen');
      
      if (open) {
        // Wenn die aktive Konversation die gleiche ist, lade Nachrichten neu UND markiere als gelesen
        // activeConversation IST die ID (Zahl), nicht ein Objekt!
        if (activeConversation && newMessage.conversation_id === activeConversation) {
          console.log('✅ CHAT: Lade Nachrichten für aktive Konversation neu UND markiere als gelesen');
          loadMessages(activeConversation);
          
          // Explizit als gelesen markieren (loadMessages macht das zwar auch, aber wir wollen sichergehen)
          markConversationAsRead(activeConversation);
        } else {
          console.log('⚠️ CHAT: Konversation nicht aktiv oder nicht die richtige', {
            activeConversation,
            messageConvId: newMessage.conversation_id,
            match: activeConversation === newMessage.conversation_id
          });
          // Nur Konversationsliste aktualisieren (zeigt unread_count)
          loadConversations();
        }
      } else {
        console.log('⚠️ CHAT: Neue Nachricht empfangen, aber Chat ist geschlossen');
        // Konversationsliste aktualisieren um unread_count zu zeigen
        loadConversations();
      }
      
      // ✅ Nachricht als verarbeitet markieren
      clearNewMessage();
    }
  }, [newMessage, open, activeConversation, clearNewMessage]);

  // WebSocket: Reagiere auf Workstation-Updates (Login/Logout)
  useEffect(() => {
    if (workstationUpdate && open) {
      console.log('🖥️ CHAT: Workstation-Update empfangen via WebSocket:', workstationUpdate);
      console.log(`   → ${workstationUpdate.workstation_name}: ${workstationUpdate.action}`);
      
      // Prüfe ob die aktuell ausgewählte Workstation betroffen ist
      if (selectedWorkstation && selectedWorkstation.id === workstationUpdate.workstation_id) {
        if (workstationUpdate.action === 'logout') {
          // Lade Workstation-Daten neu um zu prüfen ob noch User eingeloggt sind
          fetch(createApiUrl(`/chat/workstations/search`), {
            headers: getAuthHeaders()
          })
          .then(res => res.json())
          .then(workstations => {
            const updated = workstations.find(ws => ws.id === workstationUpdate.workstation_id);
            if (updated && updated.users_count === 0) {
              // Letzter User hat sich ausgeloggt
              showError(t('chat.workstation.lastUserLoggedOut', `Letzter Benutzer hat sich von @${workstationUpdate.workstation_name} ausgeloggt`));
              // Deselektiere die Workstation
              setSelectedWorkstation(null);
              setCreationMode('direct'); // Zurück zur Empfänger-Auswahl
            } else if (updated) {
              // Aktualisiere die User-Count in der ausgewählten Workstation
              setSelectedWorkstation(updated);
            }
          })
          .catch(err => console.error('Fehler beim Laden der Workstation-Daten:', err));
        } else if (workstationUpdate.action === 'login') {
          // User hat sich eingeloggt - aktualisiere Count
          fetch(createApiUrl(`/chat/workstations/search`), {
            headers: getAuthHeaders()
          })
          .then(res => res.json())
          .then(workstations => {
            const updated = workstations.find(ws => ws.id === workstationUpdate.workstation_id);
            if (updated) {
              setSelectedWorkstation(updated);
            }
          })
          .catch(err => console.error('Fehler beim Laden der Workstation-Daten:', err));
        }
      }
      
      // Lade Workstation-Liste neu (für Autocomplete)
      loadAvailableWorkstations();
      
      // ✅ Update als verarbeitet markieren
      clearWorkstationUpdate();
    }
  }, [workstationUpdate, open, selectedWorkstation, clearWorkstationUpdate]);

  // Load messages when conversation changes
  useEffect(() => {
    if (activeConversation) {
      loadMessages(activeConversation);
    }
  }, [activeConversation]);

  // Load conversations from backend
  const loadConversations = async () => {
    try {
      const response = await fetch(createApiUrl('/chat/conversations'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  // Load messages for a conversation
  const loadMessages = async (conversationId) => {
    try {
      const response = await fetch(createApiUrl(`/chat/conversations/${conversationId}/messages`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // Transform snake_case to camelCase for frontend
        const transformedMessages = data.map(msg => ({
          ...msg,
          isOwn: msg.is_own,
          senderId: msg.sender_id,
          senderUsername: msg.sender_username,
          senderFirstName: msg.sender_first_name,
          senderLastName: msg.sender_last_name,
          senderFullName: msg.sender_full_name,
          senderAcademicDegree: msg.sender_academic_degree,
          senderPosition: msg.sender_position,
          senderJobTitle: msg.sender_job_title,
          senderGender: msg.sender_gender,
          conversationId: msg.conversation_id,
          createdAt: msg.created_at,
          updatedAt: msg.updated_at,
          timestamp: msg.created_at  // For display
        }));
        setMessages(transformedMessages);
        
        // Aktualisiere Konversationsliste, um unread_count zu aktualisieren
        // Das Backend hat die Nachrichten bereits als gelesen markiert
        loadConversations();
        
        // Aktualisiere den globalen unread_count vom Backend
        refreshUnreadCount();
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    }
  };

  // Markiere Konversation explizit als gelesen
  const markConversationAsRead = async (conversationId) => {
    try {
      console.log('📖 CHAT: Markiere Konversation als gelesen:', conversationId);
      const response = await fetch(createApiUrl(`/chat/conversations/${conversationId}/read`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        console.log('✅ CHAT: Konversation als gelesen markiert');
        // Aktualisiere Konversationsliste um unread_count zu aktualisieren
        loadConversations();
        // Aktualisiere den globalen unread_count vom Backend
        refreshUnreadCount();
      }
    } catch (error) {
      console.error('❌ CHAT: Fehler beim Markieren als gelesen:', error);
    }
  };

  // Load available users for chat creation
  const loadAvailableUsers = async () => {
    try {
      const response = await fetch(createApiUrl('/users'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // Filter out current user, inactive users, and visusadmin (user_id 1)
        const currentUser = getCurrentUser();
        const filtered = data.filter(user => user.id !== currentUser?.id && user.is_active === true && user.id !== 1);
        setAvailableUsers(filtered);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Load available workstations for chat creation
  const loadAvailableWorkstations = async () => {
    try {
      const response = await fetch(createApiUrl('/chat/workstations/search'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableWorkstations(data);
      }
    } catch (error) {
      console.error('Error loading workstations:', error);
    }
  };

  const handleSendMessage = async () => {
    // Check if we're sending to a workstation or a conversation
    if (selectedWorkstation) {
      // Send to workstation
      if (message.trim() === '' && !selectedImage && !pendingLink) return;
      
      try {
        const messageData = {
          workstation_id: selectedWorkstation.id,
          text: message,
          image: selectedImage ? imagePreview : null,
          link: pendingLink
        };

        const response = await fetch(createApiUrl(`/chat/workstations/${selectedWorkstation.id}/messages`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(messageData)
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Message sent to workstation:', result);
          
          // Reset fields and exit creation mode
          setMessage('');
          setSelectedImage(null);
          setImagePreview(null);
          setPendingLink(null);
          const workstationName = selectedWorkstation.short_name;
          setSelectedWorkstation(null);
          setCreationMode(null);
          
          // Show success message
          showSuccess(`Nachricht an ${result.recipients_count} MTR(s) bei ${workstationName} gesendet`);
          
          // Reload conversations to show new chats
          await loadConversations();
        } else {
          const error = await response.json();
          showError(error.detail || 'Failed to send message to workstation');
        }
      } catch (error) {
        console.error('Error sending message to workstation:', error);
        showError('Failed to send message to workstation');
      }
      return;
    }
    
    // Normal conversation message
    if (!activeConversation || (message.trim() === '' && !selectedImage && !pendingLink)) return;

    try {
      const messageData = {
        text: message,
        image: selectedImage ? imagePreview : null,
        link: pendingLink
      };

      const response = await fetch(createApiUrl(`/chat/conversations/${activeConversation}/messages`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(messageData)
      });

      if (response.ok) {
        const newMessage = await response.json();
        // Transform snake_case to camelCase
        const transformedMessage = {
          ...newMessage,
          isOwn: newMessage.is_own,
          senderId: newMessage.sender_id,
          senderUsername: newMessage.sender_username,
          senderFirstName: newMessage.sender_first_name,
          senderLastName: newMessage.sender_last_name,
          senderFullName: newMessage.sender_full_name,
          senderAcademicDegree: newMessage.sender_academic_degree,
          senderPosition: newMessage.sender_position,
          senderJobTitle: newMessage.sender_job_title,
          senderGender: newMessage.sender_gender,
          conversationId: newMessage.conversation_id,
          createdAt: newMessage.created_at,
          updatedAt: newMessage.updated_at,
          timestamp: newMessage.created_at  // For display
        };
        setMessages([...messages, transformedMessage]);
        
        // Update last message in conversations without reloading all
        const updatedConversations = conversations.map(conv => {
          if (conv.id === activeConversation) {
            return {
              ...conv,
              last_message: transformedMessage.text || 'Anhang',
              last_message_at: transformedMessage.created_at,
              timestamp: transformedMessage.created_at
            };
          }
          return conv;
        });
        setConversations(updatedConversations);
        
        // Reset input fields
        setMessage('');
        setSelectedImage(null);
        setImagePreview(null);
        setPendingLink(null);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (date) => {
    if (!date) return '';
    
    // Backend sendet UTC-Zeit ohne 'Z'-Suffix
    // Wir müssen sie als UTC interpretieren und zu lokaler Zeit konvertieren
    let dateObj;
    if (typeof date === 'string' && !date.endsWith('Z') && !date.includes('+')) {
      // Füge 'Z' hinzu, um als UTC zu interpretieren
      dateObj = new Date(date + 'Z');
    } else {
      dateObj = date instanceof Date ? date : new Date(date);
    }
    
    if (isNaN(dateObj.getTime())) return '';
    
    // Nutze aktuelle lokale Zeit für Vergleich
    const now = new Date();
    const diff = now - dateObj;
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Gerade eben';
    if (minutes < 60) return `vor ${minutes} Min.`;
    if (hours < 24) return `vor ${hours} Std.`;
    
    return dateObj.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortTimestamp = (date) => {
    if (!date) return '';
    
    // Backend sendet UTC-Zeit ohne 'Z'-Suffix
    // Wir müssen sie als UTC interpretieren und zu lokaler Zeit konvertieren
    let dateObj;
    if (typeof date === 'string' && !date.endsWith('Z') && !date.includes('+')) {
      // Füge 'Z' hinzu, um als UTC zu interpretieren
      dateObj = new Date(date + 'Z');
    } else {
      dateObj = date instanceof Date ? date : new Date(date);
    }
    
    if (isNaN(dateObj.getTime())) return '';
    
    // Nutze aktuelle lokale Zeit für Vergleich
    const now = new Date();
    const diff = now - dateObj;
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'jetzt';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    
    const isToday = dateObj.toDateString() === now.toDateString();
    const isYesterday = new Date(now - 86400000).toDateString() === dateObj.toDateString();
    
    if (isToday) return dateObj.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    if (isYesterday) return 'Gestern';
    
    return dateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    
    const parts = name.trim().split(' ').filter(p => p.length > 0);
    if (parts.length === 0) return '?';
    
    // Filter out common academic titles/degrees
    const academicTitles = ['dr.', 'dr', 'prof.', 'prof', 'med.', 'med', 'phd', 'ph.d.', 'dipl.', 'dipl'];
    const filteredParts = parts.filter(part => 
      !academicTitles.includes(part.toLowerCase().replace(/\./g, ''))
    );
    
    if (filteredParts.length === 0) return parts[0][0].toUpperCase();
    
    // Nimm ersten Buchstaben von erstem und letztem Teil (max 2 Buchstaben)
    if (filteredParts.length === 1) {
      return filteredParts[0][0].toUpperCase();
    }
    
    return (filteredParts[0][0] + filteredParts[filteredParts.length - 1][0]).toUpperCase();
  };

  // Generate smart message preview based on message type and sender
  const getMessagePreview = (conv) => {
    const currentUser = getCurrentUser();
    const isOwnMessage = conv.last_message_sender_id === currentUser?.id;
    const isGroup = conv.type === 'group';
    const senderName = conv.last_message_sender_name || 'Unknown';
    
    // If there's no message type, return the last message text
    if (!conv.last_message_type) {
      return conv.last_message || '';
    }
    
    switch (conv.last_message_type) {
      case 'text':
        // For text messages, show the message content
        return conv.last_message || t('chat.preview.text', 'Nachricht');
      
      case 'survey':
        // For surveys:
        // - If someone has responded (last_message_has_responses) AND it's unread: show response notification
        //   - For creator: "Hat auf Umfrage geantwortet"
        //   - For group members: "[Name] hat auf Umfrage geantwortet"
        // - Otherwise: show "Umfrage: [question]"
        if (conv.last_message_has_responses && conv.unread_count > 0) {
          // Someone has responded to the survey
          if (isOwnMessage) {
            // I created it and someone responded
            return t('chat.preview.surveyResponse', 'Hat auf Umfrage geantwortet');
          } else if (isGroup) {
            // In a group, someone else responded
            return t('chat.preview.surveyResponseBy', '{{name}} hat auf Umfrage geantwortet', { name: senderName });
          } else {
            // Direct chat, other person responded
            return t('chat.preview.surveyResponse', 'Hat auf Umfrage geantwortet');
          }
        } else {
          // New survey or no responses yet - show question with prefix
          const prefix = t('chat.preview.surveyPrefix', 'Umfrage: ');
          const question = conv.last_message || t('chat.preview.survey', '📊 Umfrage');
          return prefix + question;
        }
      
      case 'event':
        return conv.last_message || t('chat.preview.event', '📅 Ereignis');
      
      case 'image':
        return t('chat.preview.image', '📷 Bild');
      
      case 'link':
        return conv.last_message || t('chat.preview.link', '🔗 Link');
      
      default:
        return conv.last_message || '';
    }
  };

  // Get CSS class for avatar based on type (user or group)
  const getAvatarClass = (type) => {
    if (type === 'group') return 'chat-group-avatar';
    return 'chat-user-avatar'; // Standard user avatar
  };

  // Get CSS class for message bubble
  const getBubbleClass = () => {
    return 'chat-message-bubble'; // Standard message bubble
  };

  const filteredConversations = conversations.filter(conv => 
    conv.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeConv = conversations.find(c => c.id === activeConversation);

  const handleConversationSelect = (convId) => {
    setActiveConversation(convId);
    setCreationMode(null); // Exit creation mode
    setSelectedUsers([]);
    setSelectedWorkstation(null);
    setUserSearchQuery('');
    setGroupName('');
  };

  // Start creating new direct chat
  const handleStartDirectChat = () => {
    setCreationMode('direct');
    setActiveConversation(null);
    setMessages([]);
    setSelectedUsers([]);
    setSelectedWorkstation(null);
    setUserSearchQuery('');
  };

  // Start creating new group chat
  const handleStartGroupChat = () => {
    setCreationMode('group');
    setActiveConversation(null);
    setMessages([]);
    setSelectedUsers([]);
    setUserSearchQuery('');
    setGroupName('');
  };

  // Create direct conversation when user is selected
  const handleUserSelect = async (user) => {
    if (!user || creationMode !== 'direct') return;

    try {
      const currentUser = getCurrentUser();
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
      const conversationData = {
        name: `${user.academic_degree || ''} ${fullName}`.trim(),
        type: 'direct',
        member_ids: [currentUser.id, user.id]
      };

      const response = await fetch(createApiUrl('/chat/conversations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(conversationData)
      });

      if (response.ok) {
        const conversation = await response.json();
        await loadConversations();
        setActiveConversation(conversation.id);
        setCreationMode(null);
        setUserSearchQuery('');
      }
    } catch (error) {
      console.error('Error creating direct chat:', error);
    }
  };

  // Handle workstation selection
  const handleWorkstationSelect = (workstation) => {
    if (!workstation || creationMode !== 'direct') return;
    
    // Prüfe ob User eingeloggt sind
    if (!workstation.users_count || workstation.users_count === 0) {
      showError(t('chat.workstation.noUsersLoggedIn', `Kein MTR an @${workstation.short_name} angemeldet`));
      return;
    }
    
    // Set workstation as selected recipient (no conversation needed)
    setSelectedWorkstation(workstation);
    setUserSearchQuery('');
  };

  // Add user to group selection
  const handleAddUserToGroup = (user) => {
    if (!user || selectedUsers.find(u => u.id === user.id)) return;
    setSelectedUsers([...selectedUsers, user]);
    setUserSearchQuery('');
  };

  // Remove user from group selection
  const handleRemoveUserFromGroup = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  // Create group conversation
  const handleCreateGroup = async () => {
    if (selectedUsers.length === 0 || !groupName.trim()) return;

    try {
      const currentUser = getCurrentUser();
      const conversationData = {
        name: groupName,
        type: 'group',
        member_ids: [currentUser.id, ...selectedUsers.map(u => u.id)]
      };

      const response = await fetch(createApiUrl('/chat/conversations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(conversationData)
      });

      if (response.ok) {
        const conversation = await response.json();
        await loadConversations();
        setActiveConversation(conversation.id);
        setCreationMode(null);
        setSelectedUsers([]);
        setUserSearchQuery('');
        setGroupName('');
      }
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  // Cancel creation mode
  const handleCancelCreation = () => {
    setCreationMode(null);
    setSelectedUsers([]);
    setUserSearchQuery('');
    setGroupName('');
    if (conversations.length > 0) {
      setActiveConversation(conversations[0].id);
    }
  };

  const handleAttachmentMenuOpen = (event) => {
    setAttachmentMenuAnchor(event.currentTarget);
  };

  const handleAttachmentMenuClose = () => {
    setAttachmentMenuAnchor(null);
  };

  const handleAttachmentAction = (action) => {
    console.log('Attachment action:', action);
    handleAttachmentMenuClose();
    
    if (action === 'patient') {
      // Patientenkontext hinzufügen
      handleAddPatientContext();
    } else if (action === 'image') {
      // Trigger file input
      imageInputRef.current?.click();
    } else if (action === 'survey') {
      handleSurveyDialogOpen();
    } else if (action === 'event') {
      handleEventDialogOpen();
    } else {
      console.log(`Action ${action} coming soon...`);
    }
  };
  
  // Patientenkontext hinzufügen
  const handleAddPatientContext = () => {
    if (!hasContext) {
      showError(
        t('chat.context.noContext', 'Kein Patient-Kontext verfügbar'),
        'ERR-CHAT-001'
      );
      return;
    }
    
    const link = createContextLink();
    console.log('📎 CHAT: Patientenkontext-Link erstellt:');
    console.log('   Link:', link);
    console.log('   reportId:', link?.reportId);
    console.log('   examId:', link?.examId);
    console.log('   type:', link?.type);
    console.log('   currentContext:', currentContext);
    console.log('   hasReportId:', !!link?.reportId);
    
    // Prüfe bei Approval-Links ob Chatpartner berechtigt ist
    if (link && link.type === 'approval') {
      const activeConv = conversations.find(c => c.id === activeConversation);
      
      if (activeConv) {
        const currentUser = getCurrentUser();
        
        // Bei Direktnachrichten: Prüfe den anderen User
        if (activeConv.type === 'direct' && activeConv.members) {
          const otherMember = activeConv.members.find(m => m.user_id !== currentUser?.id);
          if (otherMember && !otherMember.can_approve) {
            showError(
              t('profile.chat.errors.noApprovalPermission'),
              'ERR-CHAT-002'
            );
            return;
          }
        }
        
        // Bei Gruppen: Prüfe ob mindestens ein Mitglied can_approve hat
        if (activeConv.type === 'group' && activeConv.members) {
          const approvers = activeConv.members.filter(m => 
            m.user_id !== currentUser?.id && m.can_approve
          );
          if (approvers.length === 0) {
            showError(
              t('profile.chat.errors.noApprovalPermission'),
              'ERR-CHAT-002'
            );
            return;
          }
        }
      }
    }
    
    if (link) {
      setPendingLink(link);
    }
  };
  
  // Pending Link entfernen
  const handleRemovePendingLink = () => {
    setPendingLink(null);
  };
  
  // Typ-Label für Link basierend auf Sprache
  const getLinkTypeLabel = (type) => {
    if (type === 'approval') {
      return t('chat.context.approval', 'Indikationsprüfung');
    } else if (type === 'report') {
      return t('chat.context.report', 'Befund');
    }
    return type;
  };
  
  // Link-Klick Handler
  const handleLinkClick = async (link) => {
    if (!link || !link.url) return;
    
    console.log('🔗 CHAT: handleLinkClick aufgerufen', {
      linkType: link.type,
      linkUrl: link.url,
      reportId: link.reportId,
      examId: link.examId
    });
    
    // Bei Report-Links: Setze current_reader BEVOR Navigation
    if (link.type === 'report' && link.examId) {
      try {
        const currentUser = getCurrentUser();
        console.log('📝 CHAT: Setze current_reader für Report (über examId)', {
          examId: link.examId,
          newCurrentReader: currentUser.id,
          newCurrentReaderName: currentUser.username
        });
        
        // Finde zuerst die new_report_id über die examId
        const newReportResponse = await fetch(
          createApiUrl(`new-reports/by-exam/${link.examId}`),
          { headers: getAuthHeaders() }
        );
        
        if (newReportResponse.ok) {
          const newReport = await newReportResponse.json();
          console.log('📋 CHAT: new_report gefunden:', newReport);
          
          // Jetzt setze current_reader mit der new_report_id
          const url = createApiUrl(`reports/${newReport.id}/assign-current-reader/${currentUser.id}`);
          console.log('📡 CHAT: API-Call zu:', url);
          
          const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders()
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('✅ CHAT: current_reader erfolgreich gesetzt', result);
          } else {
            console.error('❌ CHAT: Fehler beim Zuweisen des Reports:', response.status, response.statusText);
            const errorText = await response.text();
            console.error('❌ CHAT: Error Response:', errorText);
          }
        } else {
          console.error('❌ CHAT: new_report nicht gefunden für examId:', link.examId);
        }
      } catch (error) {
        console.error('❌ CHAT: Exception beim Zuweisen des Reports:', error);
        // Trotzdem navigieren
      }
    }
    
    console.log('🚀 CHAT: Navigiere zu:', link.url);
    // Navigation zum Ziel
    navigate(link.url);
    
    // Chat schließen
    onClose();
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
    
    // Reset input
    event.target.value = '';
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleImageClick = (imageSrc) => {
    setImageDialogSrc(imageSrc);
    setImageDialogOpen(true);
  };

  const handleImageDialogClose = () => {
    setImageDialogOpen(false);
    setImageDialogSrc(null);
  };

  // Survey dialog handlers
  const handleSurveyDialogOpen = () => {
    setSurveyDialogOpen(true);
    setSurveyQuestion('');
    setSurveyOptions(['']);
    setSurveyMultipleChoice(false);
    setCurrentOptionIndex(0);
  };

  const handleSurveyDialogClose = () => {
    setSurveyDialogOpen(false);
  };

  const handleSurveyOptionChange = (index, value) => {
    const newOptions = [...surveyOptions];
    newOptions[index] = value;
    setSurveyOptions(newOptions);
  };

  const handleSurveyOptionKeyPress = (event, index) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      // Add new option if current option is not empty
      if (surveyOptions[index].trim() !== '') {
        const newOptions = [...surveyOptions];
        newOptions.splice(index + 1, 0, '');
        setSurveyOptions(newOptions);
        setCurrentOptionIndex(index + 1);
        // Focus next field after a short delay
        setTimeout(() => {
          const nextField = document.querySelector(`input[data-option-index="${index + 1}"]`);
          if (nextField) nextField.focus();
        }, 0);
      }
    }
  };

  const handleRemoveSurveyOption = (index) => {
    if (surveyOptions.length > 1) {
      const newOptions = surveyOptions.filter((_, i) => i !== index);
      setSurveyOptions(newOptions);
    }
  };

  const handleSendSurvey = async () => {
    if (!activeConversation || surveyQuestion.trim() === '') return;
    
    // Filter out empty options
    const validOptions = surveyOptions.filter(opt => opt.trim() !== '');
    if (validOptions.length < 2) return;

    try {
      const surveyData = {
        question: surveyQuestion,
        options: validOptions,
        multipleChoice: surveyMultipleChoice,
        votes: validOptions.map(() => []) // Empty votes initially
      };

      const response = await fetch(createApiUrl(`/chat/conversations/${activeConversation}/messages`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          survey: surveyData
        })
      });

      if (response.ok) {
        const newMessage = await response.json();
        // Transform snake_case to camelCase
        const transformedMessage = {
          ...newMessage,
          isOwn: newMessage.is_own,
          senderId: newMessage.sender_id,
          senderUsername: newMessage.sender_username,
          senderFirstName: newMessage.sender_first_name,
          senderLastName: newMessage.sender_last_name,
          senderFullName: newMessage.sender_full_name,
          senderAcademicDegree: newMessage.sender_academic_degree,
          senderPosition: newMessage.sender_position,
          senderJobTitle: newMessage.sender_job_title,
          senderGender: newMessage.sender_gender,
          conversationId: newMessage.conversation_id,
          createdAt: newMessage.created_at,
          updatedAt: newMessage.updated_at,
          timestamp: newMessage.created_at  // For display
        };
        setMessages([...messages, transformedMessage]);
        
        // Update last message in conversations without reloading all
        const updatedConversations = conversations.map(conv => {
          if (conv.id === activeConversation) {
            return {
              ...conv,
              last_message: 'Umfrage',
              last_message_at: transformedMessage.created_at,
              timestamp: transformedMessage.created_at
            };
          }
          return conv;
        });
        setConversations(updatedConversations);
        
        handleSurveyDialogClose();
      }
    } catch (error) {
      console.error('Error sending survey:', error);
    }
  };

  // Handle showing voters
  const handleShowVoters = (voterIds, optionText) => {
    // TODO: Fetch voter details from backend
    // For now, just show voter IDs
    const voters = voterIds.map(id => ({ id, firstName: `User ${id}`, lastName: '' }));
    setSelectedVoters(voters);
    setSelectedOptionText(optionText);
    setVotersDialogOpen(true);
  };

  // Event dialog handlers
  const handleEventDialogOpen = () => {
    setEventDialogOpen(true);
    setEventTitle('');
    setEventDescription('');
    setEventLocation('');
    setEventStartDate('');
    setEventEndDate('');
    setEventStartTime('');
    setEventEndTime('');
  };

  const handleEventDialogClose = () => {
    setEventDialogOpen(false);
  };

  const handleEventStartDateChange = (date) => {
    setEventStartDate(date);
    // Automatically set end date to same as start date if end date is empty or before start date
    if (!eventEndDate || eventEndDate < date) {
      setEventEndDate(date);
    }
  };

  const handleEventStartTimeChange = (time) => {
    setEventStartTime(time);
    // Automatically set end time to 1 hour after start time
    if (time && (!eventEndTime || eventEndTime <= time)) {
      const [hours, minutes] = time.split(':');
      const endHour = (parseInt(hours) + 1) % 24;
      const endTimeString = `${String(endHour).padStart(2, '0')}:${minutes}`;
      setEventEndTime(endTimeString);
    }
  };

  const handleSendEvent = async () => {
    if (!activeConversation || eventTitle.trim() === '' || eventStartDate === '' || eventStartTime === '') return;

    try {
      const eventData = {
        title: eventTitle,
        description: eventDescription,
        location: eventLocation,
        startDate: eventStartDate,
        endDate: eventEndDate || eventStartDate,
        startTime: eventStartTime,
        endTime: eventEndTime || eventStartTime
      };

      const response = await fetch(createApiUrl(`/chat/conversations/${activeConversation}/messages`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          event: eventData
        })
      });

      if (response.ok) {
        const newMessage = await response.json();
        // Transform snake_case to camelCase
        const transformedMessage = {
          ...newMessage,
          isOwn: newMessage.is_own,
          senderId: newMessage.sender_id,
          senderUsername: newMessage.sender_username,
          senderFirstName: newMessage.sender_first_name,
          senderLastName: newMessage.sender_last_name,
          senderFullName: newMessage.sender_full_name,
          senderAcademicDegree: newMessage.sender_academic_degree,
          senderPosition: newMessage.sender_position,
          senderJobTitle: newMessage.sender_job_title,
          senderGender: newMessage.sender_gender,
          conversationId: newMessage.conversation_id,
          createdAt: newMessage.created_at,
          updatedAt: newMessage.updated_at,
          timestamp: newMessage.created_at  // For display
        };
        setMessages([...messages, transformedMessage]);
        
        // Update last message in conversations without reloading all
        const updatedConversations = conversations.map(conv => {
          if (conv.id === activeConversation) {
            return {
              ...conv,
              last_message: transformedMessage.event?.title || 'Ereignis',
              last_message_at: transformedMessage.created_at,
              timestamp: transformedMessage.created_at
            };
          }
          return conv;
        });
        setConversations(updatedConversations);
        
        handleEventDialogClose();
      }
    } catch (error) {
      console.error('Error sending event:', error);
    }
  };

  // Generate ICS file for calendar import
  const generateICS = (event) => {
    const formatICSDate = (date, time) => {
      const dateTime = new Date(`${date}T${time}`);
      return dateTime.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const startDateTime = formatICSDate(event.startDate, event.startTime);
    const endDateTime = formatICSDate(event.endDate || event.startDate, event.endTime);
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Reporting System//Chat Event//DE',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${now}@reporting-chat`,
      `DTSTAMP:${now}`,
      `DTSTART:${startDateTime}`,
      `DTEND:${endDateTime}`,
      `SUMMARY:${event.title}`,
      event.description ? `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}` : '',
      event.location ? `LOCATION:${event.location}` : '',
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line !== '').join('\r\n');

    return icsContent;
  };

  const downloadICS = (event) => {
    const icsContent = generateICS(event);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // Handle survey vote
  const handleSurveyVote = async (messageId, optionIndex, currentVotes) => {
    const voteKey = `${messageId}`;
    const userVotes = surveyVotes[voteKey] || [];
    
    // Check if option is already voted
    const isVoted = userVotes.includes(optionIndex);
    
    let newUserVotes;
    if (isVoted) {
      // Remove vote
      newUserVotes = userVotes.filter(v => v !== optionIndex);
    } else {
      // Add vote
      const msg = messages.find(m => m.id === messageId);
      if (msg && msg.survey && msg.survey.multipleChoice) {
        // Multiple choice: add to existing votes
        newUserVotes = [...userVotes, optionIndex];
      } else {
        // Single choice: replace existing vote
        newUserVotes = [optionIndex];
      }
    }
    
    try {
      // Send vote to backend
      const response = await fetch(createApiUrl(`/chat/messages/${messageId}/vote`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          option_indices: newUserVotes
        })
      });

      if (response.ok) {
        // Update local vote state
        setSurveyVotes({
          ...surveyVotes,
          [voteKey]: newUserVotes
        });
        
        // Get updated message from response
        const updatedMessage = await response.json();
        
        // Transform snake_case to camelCase for frontend
        const transformedMessage = {
          ...updatedMessage,
          isOwn: updatedMessage.is_own,
          senderId: updatedMessage.sender_id,
          senderUsername: updatedMessage.sender_username,
          senderFirstName: updatedMessage.sender_first_name,
          senderLastName: updatedMessage.sender_last_name,
          senderFullName: updatedMessage.sender_full_name,
          senderAcademicDegree: updatedMessage.sender_academic_degree,
          senderPosition: updatedMessage.sender_position,
          senderJobTitle: updatedMessage.sender_job_title,
          senderGender: updatedMessage.sender_gender,
          conversationId: updatedMessage.conversation_id,
          createdAt: updatedMessage.created_at,
          updatedAt: updatedMessage.updated_at,
          timestamp: updatedMessage.created_at
        };
        
        // Update the message in the local messages array
        setMessages(messages.map(msg => 
          msg.id === messageId ? transformedMessage : msg
        ));
        
        // Reload conversations to update unread count
        // Backend ensures that the voter's last_read_at is updated, so they won't see it as unread
        // Small delay to ensure backend has processed the update
        setTimeout(() => {
          loadConversations();
        }, 100);
      }
    } catch (error) {
      console.error('Error voting on survey:', error);
    }
  };

  // Helper function to render text with underlined shortcut key
  const renderTextWithShortcut = (text, key) => {
    const index = text.toUpperCase().indexOf(key.toUpperCase());
    if (index === -1) return text;

  return (
      <>
        {text.substring(0, index)}
        <u>{text.substring(index, index + 1)}</u>
        {text.substring(index + 1)}
      </>
    );
  };

  // Keyboard shortcuts for attachment menu
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle shortcuts when chat dialog is open
      if (event.altKey && open) {
        const key = event.key.toUpperCase();
        
        // Map keys to actions based on current language
        const keyMap = {
          [t('chat.attachmentMenu.patientContextKey').toUpperCase()]: 'patient',
          [t('chat.attachmentMenu.imageKey').toUpperCase()]: 'image',
          [t('chat.attachmentMenu.surveyKey').toUpperCase()]: 'survey',
          [t('chat.attachmentMenu.eventKey').toUpperCase()]: 'event'
        };
        
        if (keyMap[key]) {
          event.preventDefault();
          handleAttachmentAction(keyMap[key]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [attachmentMenuAnchor, open, t]);

  // Debug: Dialog state changes
  useEffect(() => {
    if (open) {
      console.log('🚪 DEBUG DIALOG: Main Chat Dialog OPENED');
    } else {
      console.log('🚪 DEBUG DIALOG: Main Chat Dialog CLOSED');
    }
  }, [open]);

  useEffect(() => {
    if (activeConversation) {
      console.log(`💬 DEBUG DIALOG: Active Conversation SET to ${activeConversation}`);
    } else {
      console.log('💬 DEBUG DIALOG: Active Conversation CLEARED (null)');
    }
  }, [activeConversation]);

  useEffect(() => {
    if (creationMode) {
      console.log(`➕ DEBUG DIALOG: Creation Mode SET to "${creationMode}"`);
    } else {
      console.log('➕ DEBUG DIALOG: Creation Mode CLEARED (null)');
    }
  }, [creationMode]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        console.log(`🚪 DEBUG DIALOG: Main Chat Dialog onClose triggered, reason: ${reason}`);
        onClose(event, reason);
      }}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: '85vh'
        }
      }}
    >
      {/* Dialog Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatOutlined color="primary" />
          {t('chat.title', 'Chat')}
        </Typography>
        <Tooltip title={t('chat.closeChat', 'Chat schließen')}>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Chat Content */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        overflow: 'hidden',
        flex: 1
      }}>
        {/* Sidebar */}
        {sidebarOpen && (
          <Box sx={{ 
            width: 320, 
            borderRight: 1, 
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Sidebar Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('chat.searchConversations', 'Chats suchen...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 1.5 }}
              />
              
              {/* Button Bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={t('chat.newChat', 'Neuer Chat')}>
                    <IconButton
                      onClick={handleStartDirectChat}
                      className="chat-new-button"
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('chat.newGroup', 'Neue Gruppe')}>
                    <IconButton
                      onClick={handleStartGroupChat}
                      className="chat-group-button"
                    >
                      <GroupAddIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Tooltip title={sidebarOpen ? t('chat.hideSidebar', 'Seitenleiste ausblenden') : t('chat.showSidebar', 'Seitenleiste einblenden')}>
                  <IconButton
                    onClick={() => setSidebarOpen(false)}
        sx={{ 
                      bgcolor: 'action.hover',
                      '&:hover': {
                        bgcolor: 'action.selected'
                      }
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Conversations List */}
            <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
              {filteredConversations.map((conv) => (
                <ListItemButton
                  key={conv.id}
                  selected={conv.id === activeConversation}
                  onClick={() => handleConversationSelect(conv.id)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      '&:hover': {
                        bgcolor: 'action.selected'
                      }
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Badge 
                      badgeContent={conv.unread_count} 
                      color="error"
                      overlap="circular"
                    >
                      <Badge
                        variant="dot"
                        color="success"
                        invisible={!conv.isOnline}
                        overlap="circular"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                      >
                        <Avatar 
                          className={getAvatarClass(conv.type)}
                          sx={{ fontSize: '12px' }}
                        >
                          {conv.type === 'group' ? <GroupIcon /> : getInitials(conv.name)}
                        </Avatar>
                      </Badge>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: conv.unread_count > 0 ? 'bold' : 'normal' }}>
                          {conv.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatShortTimestamp(conv.timestamp)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: conv.unread_count > 0 ? 'bold' : 'normal'
                        }}
                      >
                        {getMessagePreview(conv)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}

        {/* Toggle Sidebar Button (only when sidebar is closed) */}
        {!sidebarOpen && (
          <Tooltip title={t('chat.showSidebar', 'Seitenleiste einblenden')}>
            <IconButton
              onClick={() => setSidebarOpen(true)}
        sx={{ 
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Main Chat Area */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Chat Header */}
          {creationMode ? (
            <Box sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider'
            }}>
              {creationMode === 'direct' ? (
                // Direct Chat Creation: User Search
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="h6">
                      {t('chat.newChat', 'Neuer Chat')}
                    </Typography>
                  </Box>
                  <Autocomplete
                    fullWidth
                    options={[
                      ...availableWorkstations.map(ws => ({ ...ws, itemType: 'workstation' })),
                      ...availableUsers.map(u => ({ ...u, itemType: 'user' }))
                    ]}
                    getOptionLabel={(item) => {
                      if (item.itemType === 'workstation') {
                        return `@${item.short_name} - ${item.name}`;
                      }
                      const fullName = `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.username;
                      return `${item.academic_degree || ''} ${fullName}`.trim();
                    }}
                    groupBy={(item) => item.itemType === 'workstation' ? t('chat.workstations', 'Arbeitsplätze') : t('chat.users', 'Benutzer')}
                    onChange={(event, value) => {
                      if (!value) return;
                      if (value.itemType === 'workstation') {
                        handleWorkstationSelect(value);
                      } else {
                        handleUserSelect(value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoFocus
                        label={t('chat.selectRecipient', 'Empfänger auswählen')}
                        variant="outlined"
                        size="small"
                        placeholder={t('chat.searchUsersOrWorkstations', 'Benutzer oder @Arbeitsplätze suchen...')}
                      />
                    )}
                    renderOption={(props, item) => {
                      if (item.itemType === 'workstation') {
                        return (
                          <li {...props}>
                            <ComputerIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                @{item.short_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.name} • {item.users_count} {item.users_count === 1 ? 'MTR' : 'MTRs'}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }
                      const fullName = `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.username;
                      const initials = getInitials(fullName);
                      return (
                        <li {...props}>
                          <Avatar sx={{ mr: 1, width: 32, height: 32, fontSize: '12px' }}>
                            {initials}
                          </Avatar>
                          {item.academic_degree} {fullName}
                        </li>
                      );
                    }}
                  />
                </Box>
              ) : (
                // Group Chat Creation: Multi-User Search + Group Name
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <GroupIcon color="primary" />
                    <Typography variant="h6">
                      {t('chat.newGroup', 'Neue Gruppe')}
                    </Typography>
                  </Box>
                  
                  <TextField
                    fullWidth
                    autoFocus
                    label={t('chat.groupName', 'Gruppenname')}
                    variant="outlined"
                    size="small"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <Autocomplete
                    fullWidth
                    options={availableUsers.filter(u => !selectedUsers.find(su => su.id === u.id))}
                    getOptionLabel={(user) => {
                      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
                      return `${user.academic_degree || ''} ${fullName}`.trim();
                    }}
                    value={null}
                    inputValue={userSearchQuery}
                    onInputChange={(event, value) => setUserSearchQuery(value)}
                    onChange={(event, value) => {
                      if (value) handleAddUserToGroup(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('chat.selectMembers', 'Mitglieder auswählen')}
                        variant="outlined"
                        size="small"
                        placeholder={t('chat.searchAndPressEnter', 'Suchen und Enter drücken')}
                      />
                    )}
                    renderOption={(props, user) => {
                      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
                      const initials = getInitials(fullName);
                      return (
                        <li {...props}>
                          <Avatar sx={{ mr: 1, width: 32, height: 32, fontSize: '12px' }}>
                            {initials}
                          </Avatar>
                          {user.academic_degree} {fullName}
                        </li>
                      );
                    }}
                  />
                  
                  {/* Selected Users */}
                  {selectedUsers.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedUsers.map(user => {
                        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
                        const initials = getInitials(fullName);
                        return (
                          <Chip
                            key={user.id}
                            avatar={<Avatar sx={{ fontSize: '12px' }}>{initials}</Avatar>}
                            label={`${user.academic_degree || ''} ${fullName}`.trim()}
                            onDelete={() => handleRemoveUserFromGroup(user.id)}
                            color="primary"
                            variant="outlined"
                          />
                        );
                      })}
                    </Box>
                  )}
                  
                  {/* Create Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    className="button"
                    onClick={handleCreateGroup}
                    disabled={selectedUsers.length === 0 || !groupName.trim()}
                    sx={{ mt: 2 }}
                  >
                    {t('common.create', 'Erstellen')}
                  </Button>
                </Box>
              )}
            </Box>
          ) : selectedWorkstation ? (
            // Workstation Chat Header
            <Box sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <ComputerIcon color="primary" sx={{ fontSize: 40 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">
                  @{selectedWorkstation.short_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedWorkstation.name} • {selectedWorkstation.users_count} {selectedWorkstation.users_count === 1 ? 'MTR' : 'MTRs'} {t('chat.workstation.loggedIn', 'eingeloggt')}
                </Typography>
              </Box>
            </Box>
          ) : activeConv && (
            <Box sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Avatar className={getAvatarClass(activeConv.type)} sx={{ fontSize: '12px' }}>
                {activeConv.type === 'group' ? <GroupIcon /> : getInitials(activeConv.name)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">
                  {activeConv.name}
                </Typography>
                {activeConv.type === 'group' && activeConv.members && (
                  <Typography variant="caption" color="text.secondary">
                    {activeConv.members.length} {t('chat.members', 'Mitglieder')}
                  </Typography>
                )}
                {activeConv.type === 'direct' && (
                  <Typography variant="caption" color="text.secondary">
                    {activeConv.isOnline ? t('chat.online', 'Online') : t('chat.offline', 'Offline')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Messages Area */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            p: 2,
          display: 'flex', 
          flexDirection: 'column', 
            gap: 2
          }}>
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  flexDirection: msg.isOwn ? 'row-reverse' : 'row',
                  gap: 1,
                  alignItems: 'flex-start'
                }}
              >
                {/* Avatar */}
                <Avatar 
                  className={!msg.isOwn ? getAvatarClass('user') : ''}
                  sx={{ 
                    bgcolor: msg.isOwn ? 'var(--rd-primary)' : undefined,
                    width: 36,
                    height: 36,
                    fontSize: '12px'
                  }}
                >
                  {getInitials(msg.sender_full_name || msg.sender_username)}
                </Avatar>

                {/* Message Bubble */}
                <Box sx={{ maxWidth: '70%' }}>
                  <Box
                    className={!msg.isOwn ? getBubbleClass() : ''}
                    sx={{
                      bgcolor: msg.isOwn ? 'var(--rd-primary)' : undefined,
                      color: msg.isOwn ? 'white' : undefined,
                      px: 2,
                      py: 1.5,
                      borderRadius: '5px'
                    }}
                  >
                    {!msg.isOwn && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 'bold',
                          display: 'block',
                          mb: 0.5,
                          opacity: 0.9,
                          color: 'inherit'
                        }}
                      >
                        {msg.sender_full_name || msg.sender_username}
                      </Typography>
                    )}
                    {msg.text && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'inherit' }}>
                        {msg.text}
                      </Typography>
                    )}
                    
                    {msg.link && (
                      <Box
                        sx={{
                          mt: msg.text ? 1 : 0,
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.2)'
                          }
                        }}
                        onClick={() => handleLinkClick(msg.link)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 16 }} />
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                            {getLinkTypeLabel(msg.link.type)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'inherit' }}>
                          {msg.link.patientName}
                        </Typography>
                        {msg.link.studyDate && (
                          <Typography variant="caption" sx={{ opacity: 0.8, color: 'inherit' }}>
                            {msg.link.studyDate}
                            {msg.link.modality && ` • ${msg.link.modality}`}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {msg.image && (
                      <Box
                        component="img"
                        src={msg.image}
                        alt="Attachment"
                        sx={{
                          maxWidth: 300,
                          maxHeight: 300,
                          borderRadius: 1,
                          display: 'block',
                          mb: msg.text ? 1 : 0,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.9
                          }
                        }}
                        onClick={() => handleImageClick(msg.image)}
                      />
                    )}
                    {msg.survey && (
                      <Box sx={{ minWidth: 300, maxWidth: 400 }}>
                        {/* Question */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'inherit' }}>
                          {msg.survey.question}
                        </Typography>
                        
                        {/* Info text */}
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, opacity: 0.7 }}>
                          <PollIcon fontSize="small" />
                          {msg.survey.multipleChoice 
                            ? t('chat.survey.selectMinimumMultiple', 'Wähle mindestens eine Option aus.')
                            : t('chat.survey.selectMinimumSingle', 'Wähle eine Option aus.')
                          }
                        </Typography>
                        
                        {/* Options */}
                        {msg.survey.options.map((option, optIndex) => {
                          const voteKey = `${msg.id}`;
                          const userVotes = surveyVotes[voteKey] || [];
                          const isVoted = userVotes.includes(optIndex);
                          const voteCount = msg.survey.votes[optIndex]?.length || 0;
                          
                          // Calculate total votes and percentage
                          const totalVotes = msg.survey.votes.reduce((sum, voters) => sum + voters.length, 0);
                          const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                          
                          return (
                            <Box
                              key={optIndex}
                              onClick={() => handleSurveyVote(msg.id, optIndex, msg.survey.votes)}
                              sx={{
                                position: 'relative',
                                mb: 1,
                                p: 1.5,
                                borderRadius: 1,
                                cursor: 'pointer',
                                bgcolor: isVoted ? 'rgba(0, 200, 83, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                border: isVoted ? '2px solid var(--rd-success)' : '1px solid rgba(255, 255, 255, 0.1)',
                                '&:hover': {
                                  bgcolor: isVoted ? 'rgba(0, 200, 83, 0.15)' : 'rgba(255, 255, 255, 0.08)'
                                },
                                overflow: 'hidden'
                              }}
                            >
                              {/* Progress bar background */}
                              {totalVotes > 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    height: '100%',
                                    width: `${percentage}%`,
                                    bgcolor: isVoted ? 'rgba(0, 200, 83, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                    transition: 'width 0.3s ease',
                                    zIndex: 0
                                  }}
                                />
                              )}
                              
                              {/* Option content */}
                              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                  {/* Checkbox/Radio indicator */}
                                  <Box
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: msg.survey.multipleChoice ? 0.5 : '50%',
                                      border: isVoted ? 'none' : '2px solid rgba(255, 255, 255, 0.5)',
                                      bgcolor: isVoted ? 'var(--rd-success)' : 'transparent',
                                      display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
                                      flexShrink: 0
                                    }}
                                  >
                                    {isVoted && (
                                      <Box
                                        component="span"
                                        sx={{
                                          color: 'white',
                                          fontSize: 14,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        ✓
                                      </Box>
                                    )}
                                  </Box>
                                  
                                  {/* Option text */}
                                  <Typography variant="body2" sx={{ color: 'inherit', fontWeight: isVoted ? 600 : 400 }}>
                                    {option}
        </Typography>
                                </Box>
                                
                                {/* Vote count */}
                                {totalVotes > 0 && voteCount > 0 && (
                                  <Box
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShowVoters(msg.survey.votes[optIndex], option);
                                    }}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 2,
                                      cursor: 'pointer',
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                                      }
                                    }}
                                  >
                                    <PersonIcon fontSize="small" sx={{ fontSize: 14 }} />
                                    <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
                                      {voteCount}
        </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                        
                        {/* Total votes info */}
                        {(() => {
                          const totalVotes = msg.survey.votes.reduce((sum, voters) => sum + voters.length, 0);
                          return totalVotes > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {totalVotes} {totalVotes === 1 ? t('chat.survey.vote', 'Stimme') : t('chat.survey.votes', 'Stimmen')}
                            </Typography>
                          );
                        })()}
                      </Box>
                    )}
                    {msg.event && (
                      <Box sx={{ minWidth: 300, maxWidth: 400 }}>
                        {/* Event display */}
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          {/* Event Icon and Title */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <CalendarMonthIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'inherit' }}>
                              {msg.event.title}
                            </Typography>
                          </Box>

                          {/* Date and Time */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(msg.event.startDate).toLocaleDateString('de-DE', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                              {msg.event.endDate && msg.event.endDate !== msg.event.startDate && (
                                <> - {new Date(msg.event.endDate).toLocaleDateString('de-DE', { 
                                  weekday: 'long', 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })}</>
                              )}
                              {' • '}
                              {msg.event.startTime}
                              {msg.event.endTime && msg.event.endTime !== msg.event.startTime && ` - ${msg.event.endTime}`}
                            </Typography>
                          </Box>

                          {/* Location */}
                          {msg.event.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {msg.event.location}
                              </Typography>
                            </Box>
                          )}

                          {/* Description */}
                          {msg.event.description && (
                            <Typography variant="body2" sx={{ mt: 2, mb: 2, color: 'inherit', whiteSpace: 'pre-wrap' }}>
                              {msg.event.description}
                            </Typography>
                          )}

                          {/* Download Button */}
                          <Button
                            variant="contained"
                            className="button"
                            startIcon={<DownloadIcon />}
                            onClick={() => downloadICS(msg.event)}
                            fullWidth
                            sx={{ mt: 1 }}
                          >
                            {t('chat.event.downloadCalendar', 'Zu Kalender hinzufügen')}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      display: 'block',
                      mt: 0.5,
                      px: 1,
                      textAlign: msg.isOwn ? 'right' : 'left'
                    }}
                  >
                    {formatTimestamp(msg.timestamp)}
                  </Typography>
                </Box>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          {/* Input Area */}
          <Box sx={{ p: 2 }}>
            {/* Image Preview */}
            {imagePreview && (
              <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{
                    maxWidth: 200,
                    maxHeight: 200,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider'
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'error.dark'
                    }
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Patientenkontext-Link Vorschau */}
            {pendingLink && (
              <Paper 
                elevation={2}
                sx={{ 
                  mb: 2, 
                  p: 1.5, 
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  backgroundColor: 'action.hover',
                  position: 'relative'
                }}
              >
                <PersonSearchIcon color="primary" sx={{ mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                    {getLinkTypeLabel(pendingLink.type)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {pendingLink.patientName}
                  </Typography>
                  {pendingLink.studyDate && (
                    <Typography variant="caption" color="text.secondary">
                      {pendingLink.studyDate}
                      {pendingLink.modality && ` • ${pendingLink.modality}`}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={handleRemovePendingLink}
                  sx={{
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'error.dark'
                    }
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Paper>
            )}

            {/* Hidden File Input */}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageSelect}
            />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Tooltip title={t('chat.addAttachment', 'Anhang hinzufügen')}>
                <IconButton
                  className="chat-new-button"
                  sx={{ mt: 0.5 }}
                  onClick={handleAttachmentMenuOpen}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={t('chat.inputPlaceholder', 'Nachricht eingeben...')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3
                }
              }}
            />
              <IconButton 
                color="primary" 
                onClick={handleSendMessage}
                disabled={(message.trim() === '' && !selectedImage && !pendingLink) || (creationMode === 'direct' && !selectedWorkstation && !activeConversation)}
                sx={{
                  mt: 0.5,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500'
                  }
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Attachment Menu */}
      <Menu
        anchorEl={attachmentMenuAnchor}
        open={Boolean(attachmentMenuAnchor)}
        onClose={handleAttachmentMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Tooltip 
          title={isPatientContextDisabled ? t('chat.context.notAvailable', 'Not available') : ''} 
          placement="left"
        >
          <span>
            <MenuItem 
              onClick={() => handleAttachmentAction('patient')}
              disabled={isPatientContextDisabled}
            >
              <ListItemIcon>
                <PersonSearchIcon 
                  fontSize="small" 
                  color={isPatientContextDisabled ? "disabled" : "primary"} 
                />
              </ListItemIcon>
              <ListItemText>
                {renderTextWithShortcut(
                  t('chat.attachmentMenu.patientContext'),
                  t('chat.attachmentMenu.patientContextKey')
                )}
              </ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem onClick={() => handleAttachmentAction('image')}>
          <ListItemIcon>
            <ImageIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>
            {renderTextWithShortcut(
              t('chat.attachmentMenu.image'),
              t('chat.attachmentMenu.imageKey')
            )}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAttachmentAction('survey')}>
          <ListItemIcon>
            <PollIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>
            {renderTextWithShortcut(
              t('chat.attachmentMenu.survey'),
              t('chat.attachmentMenu.surveyKey')
            )}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAttachmentAction('event')}>
          <ListItemIcon>
            <EventIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>
            {renderTextWithShortcut(
              t('chat.attachmentMenu.event'),
              t('chat.attachmentMenu.eventKey')
            )}
          </ListItemText>
        </MenuItem>
      </Menu>


      {/* Hidden file input */}
      <input
        type="file"
        ref={imageInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageSelect}
      />

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={(event, reason) => {
          console.log(`🖼️ DEBUG DIALOG: Image Dialog onClose triggered, reason: ${reason}`);
          handleImageDialogClose(event, reason);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('chat.imageViewer', 'Bildanzeige')}</Typography>
            <IconButton onClick={handleImageDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {imageDialogSrc && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400
              }}
            >
              <Box
                component="img"
                src={imageDialogSrc}
                alt="Full size"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Survey Dialog */}
      <Dialog
        open={surveyDialogOpen}
        onClose={(event, reason) => {
          console.log(`📊 DEBUG DIALOG: Survey Dialog onClose triggered, reason: ${reason}`);
          handleSurveyDialogClose(event, reason);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('chat.survey.createTitle', 'Abstimmung erstellen')}</Typography>
            <IconButton onClick={handleSurveyDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Question Field */}
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="subtitle2" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
              {t('chat.survey.question', 'Frage')}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              placeholder={t('chat.survey.questionPlaceholder', 'Stelle eine Frage.')}
              value={surveyQuestion}
              onChange={(e) => setSurveyQuestion(e.target.value)}
              variant="outlined"
            />
          </Box>

          {/* Options Field */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
              {t('chat.survey.options', 'Optionen')}
        </Typography>
            {surveyOptions.map((option, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField
                  fullWidth
                  placeholder={t('chat.survey.optionPlaceholder', 'Text hinzufügen')}
                  value={option}
                  onChange={(e) => handleSurveyOptionChange(index, e.target.value)}
                  onKeyPress={(e) => handleSurveyOptionKeyPress(e, index)}
                  variant="outlined"
                  size="small"
                  inputProps={{
                    'data-option-index': index
                  }}
                />
                {surveyOptions.length > 1 && (
                  <IconButton 
                    size="small" 
                    onClick={() => handleRemoveSurveyOption(index)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>

          {/* Multiple Choice Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={surveyMultipleChoice}
                onChange={(e) => setSurveyMultipleChoice(e.target.checked)}
                color="primary"
              />
            }
            label={t('chat.survey.multipleChoice', 'Mehrere Antworten erlauben')}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={handleSendSurvey}
            variant="contained"
            className="button"
            disabled={surveyQuestion.trim() === '' || surveyOptions.filter(o => o.trim() !== '').length < 2}
            startIcon={<SendIcon />}
          >
            {t('chat.survey.send', 'Senden')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Dialog */}
      <Dialog
        open={eventDialogOpen}
        onClose={(event, reason) => {
          console.log(`📅 DEBUG DIALOG: Event Dialog onClose triggered, reason: ${reason}`);
          handleEventDialogClose(event, reason);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('chat.event.createTitle', 'Ereignis erstellen')}</Typography>
            <IconButton onClick={handleEventDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Title Field */}
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label={t('chat.event.title', 'Titel')}
              placeholder={t('chat.event.titlePlaceholder', 'Ereignis-Titel')}
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
              variant="outlined"
              required
            />
          </Box>

          {/* Date Fields */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <CustomDatePicker
              label={t('chat.event.startDate', 'Startdatum')}
              value={eventStartDate}
              onChange={(e) => handleEventStartDateChange(e.target.value)}
              required
              sx={{ flex: 1 }}
            />
            <CustomDatePicker
              label={t('chat.event.endDate', 'Enddatum')}
              value={eventEndDate}
              onChange={(e) => setEventEndDate(e.target.value)}
              required
              inputProps={{ min: eventStartDate }}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Time Fields */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <CustomTimePicker
              label={t('chat.event.startTime', 'Startzeit')}
              value={eventStartTime}
              onChange={(e) => handleEventStartTimeChange(e.target.value)}
              required
              sx={{ flex: 1 }}
            />
            <CustomTimePicker
              label={t('chat.event.endTime', 'Endzeit')}
              value={eventEndTime}
              onChange={(e) => setEventEndTime(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Location Field */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={t('chat.event.location', 'Ort')}
              placeholder={t('chat.event.locationPlaceholder', 'Veranstaltungsort')}
              value={eventLocation}
              onChange={(e) => setEventLocation(e.target.value)}
              variant="outlined"
            />
          </Box>

          {/* Description Field */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('chat.event.description', 'Beschreibung')}
              placeholder={t('chat.event.descriptionPlaceholder', 'Ereignis-Beschreibung')}
              value={eventDescription}
              onChange={(e) => setEventDescription(e.target.value)}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={handleSendEvent}
            variant="contained"
            className="button"
            disabled={eventTitle.trim() === '' || eventStartDate === '' || eventStartTime === ''}
            startIcon={<SendIcon />}
          >
            {t('chat.event.send', 'Senden')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Voters Dialog */}
      <Dialog
        open={votersDialogOpen}
        onClose={(event, reason) => {
          console.log(`👥 DEBUG DIALOG: Voters Dialog onClose triggered, reason: ${reason}`);
          setVotersDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{selectedOptionText}</Typography>
            <IconButton onClick={() => setVotersDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {selectedVoters.map((voter, index) => (
              <ListItem key={voter.id} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar 
                    className="chat-user-avatar"
                    sx={{ width: 40, height: 40, fontSize: '12px' }}
                  >
                    {voter.firstName?.charAt(0)}{voter.lastName?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body1">
                      {voter.title && `${voter.title} `}{voter.firstName} {voter.lastName}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
          {selectedVoters.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              {t('chat.survey.noVotes', 'Noch keine Stimmen')}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default Chat;

